import { Button, Input, Popover, Select, Tooltip } from 'antd';
import { isArray, isObject } from 'lodash';
import React, { useRef, useState } from 'react';
import {
  CaretDownOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';
import { v4 as uuidV4 } from 'uuid';
import FieldRegister from 'views/common/FieldRegister';
import { validateRequired } from 'utils/validate';
import TextField from 'views/common/TextField';
import useEventCallback from 'hooks/useEventCallback';

const TYPE = {
  ARRAY: 'ARRAY',
  OBJECT: 'OBJECT',
  STRING: 'STRING',
  NUMBER: 'NUMBER',
  BOOLEAN: 'BOOLEAN',
};

const PREFIX_STRING = 'STRING_';
const PREFIX_BOOLEAN = 'BOOLEAN_';
const PREFIX_NUMBER = 'NUMBER_';

const JsonItem = ({ value, placeholder, onChange, disabled }) => {
  const name = useRef(uuidV4());
  const index = value.indexOf('_');
  const prefix = value.substring(0, index + 1);
  const newValue = value.substring(index + 1);
  return (
    <div className="json-item">
      <FieldRegister
        name={name}
        validate={validateRequired('Field')}
        value={newValue}
        disabled={disabled}
        onChange={(e) => onChange(prefix + (e.target.value || ''))}
        placeholder={placeholder}
      >
        {({ value, onChange, disabled, onBlur, error, placeholder }) => (
          <TextField helperText={error}>
            <Input
              size="small"
              placeholder={placeholder}
              className="json-item__value"
              value={value}
              disabled={disabled}
              onChange={onChange}
              onBlur={onBlur}
            />
          </TextField>
        )}
      </FieldRegister>
    </div>
  );
};

const getType = (value) => {
  return isArray(value)
    ? TYPE.ARRAY
    : isObject(value)
    ? TYPE.OBJECT
    : (value || '').startsWith(PREFIX_NUMBER)
    ? TYPE.NUMBER
    : (value || '').startsWith(PREFIX_BOOLEAN)
    ? TYPE.BOOLEAN
    : TYPE.STRING;
};

const getValue = (type) => {
  switch (type) {
    case TYPE.STRING:
      return PREFIX_STRING;
    case TYPE.NUMBER:
      return PREFIX_NUMBER;
    case TYPE.BOOLEAN:
      return PREFIX_BOOLEAN;
    case TYPE.ARRAY:
      return [];
    case TYPE.OBJECT:
    default:
      return {};
  }
};

const JsonSchemeCreator = ({
  value= {},
  title,
  onChange,
  onRemove = () => {},
  isRoot = true,
  className,
  disabled,
}) => {
  const type = getType(value);
  const typeExpand = type === TYPE.OBJECT || type === TYPE.ARRAY;
  const [expand, setExpand] = useState(typeExpand);
  const [open, setOpen] = useState(false);
  const [fieldName, setFieldName] = useState();
  const [newType, setNewType] = useState(TYPE.OBJECT);

  const onChangeOpen = () => {
    setOpen(!open);
  };

  const onChangeFieldName = (e) => {
    setFieldName(e.target.value);
  };

  const onChangeType = (v) => {
    setNewType(v);
  };

  const onExpand = () => {
    if (!typeExpand) return;
    setExpand(!expand);
  };

  const onAddNewItem = () => {
    onChangeOpen();
    setFieldName();
    if (type === TYPE.ARRAY) {
      return onChange([...value, getValue(newType)]);
    }

    return onChange({ ...value, [fieldName]: getValue(newType) });
  };

  const onChangeNewType = (v) => {
    onChange(getValue(v));
  };

  const onChangeObjectKey = useEventCallback(
    (key, object) => {
      onChange({ ...value, [key]: object });
    },
    [value]
  );

  const onChangeObjectItem = useEventCallback(
    (index, object) => {
      onChange(value.map((item, i) => (index === i ? object : item)));
    },
    [value]
  );

  const onRemoveItem = (index) => {
    onChange((value || []).filter((item, i) => i !== index));
  };

  const onRemoveKey = (key) => {
    const newObject = { ...value };
    delete newObject[key];
    onChange(newObject);
  };

  return (
    <div
      className={classNames('json-schema', {
        [className]: className,
      })}
    >
      {title && (
        <div
          className={classNames('json-schema__title', {
            'json-schema__title--expand': expand,
          })}
        >
          {typeExpand && (
            <CaretDownOutlined
              onClick={onExpand}
              className="json-schema__expand-icon"
            />
          )}
          <span onClick={onExpand}>{title}</span>

          <Select
            style={{ minWidth: '6rem' }}
            size="small"
            className="json-schema__type"
            defaultValue={type}
            onChange={onChangeNewType}
            disabled={isRoot}
          >
            <Select.Option value={TYPE.OBJECT}>Object</Select.Option>
            <Select.Option value={TYPE.ARRAY}>Array</Select.Option>
            <Select.Option value={TYPE.STRING}>String</Select.Option>
            <Select.Option value={TYPE.NUMBER}>Number</Select.Option>
            <Select.Option value={TYPE.BOOLEAN}>Boolean</Select.Option>
          </Select>

          {typeExpand && !disabled && (
            <>
              <Popover
                open={open}
                onOpenChange={onChangeOpen}
                trigger="click"
                title={
                  type === TYPE.ARRAY ? 'Add new item' : 'Add new property'
                }
                content={
                  <div className="json-add">
                    {type === TYPE.OBJECT && (
                      <Input
                        placeholder="Enter field name"
                        value={fieldName}
                        onChange={onChangeFieldName}
                      />
                    )}
                    <Select
                      value={newType}
                      allowClear={false}
                      onChange={onChangeType}
                      style={{ minWidth: '6rem' }}
                    >
                      <Select.Option value={TYPE.OBJECT}>Object</Select.Option>
                      <Select.Option value={TYPE.ARRAY}>Array</Select.Option>
                      <Select.Option value={TYPE.STRING}>String</Select.Option>
                      <Select.Option value={TYPE.NUMBER}>Number</Select.Option>
                      <Select.Option value={TYPE.BOOLEAN}>
                        Boolean
                      </Select.Option>
                    </Select>
                    <Button onClick={onAddNewItem} type="primary">
                      Add
                    </Button>
                  </div>
                }
              >
                <span className="json-schema__add">
                  <Tooltip
                    title={type === TYPE.ARRAY ? 'Add item' : 'Add property'}
                  >
                    <PlusCircleOutlined />
                  </Tooltip>
                </span>
              </Popover>
            </>
          )}
          {!isRoot && !disabled && (
            <span className="json-schema__add" onClick={onRemove}>
              <Tooltip title="Remove">
                <MinusCircleOutlined />
              </Tooltip>
            </span>
          )}
        </div>
      )}
      <div
        className={classNames('json-schema__body', {
          'json-schema__body--expand': !typeExpand || expand,
          'json-schema__left-border': typeExpand,
        })}
      >
        {type === TYPE.STRING ||
        type === TYPE.NUMBER ||
        type === TYPE.BOOLEAN ? (
          <JsonItem
            placeholder="Enter description"
            onChange={onChange}
            value={value}
            onRemove={onRemove}
            disabled={disabled}
          />
        ) : type === TYPE.ARRAY ? (
          <div className="json-array">
            {(value || []).map((item, index) => (
              <JsonSchemeCreator
                key={index}
                onChange={(v) => onChangeObjectItem(index, v)}
                title={'Item: ' + index}
                value={item}
                onRemove={(v) => onRemoveItem(index)}
                isRoot={false}
                disabled={disabled}
              />
            ))}
          </div>
        ) : (
          Object.keys(value || {}).map((item) => (
            <JsonSchemeCreator
              key={item}
              onChange={(v) => onChangeObjectKey(item, v)}
              value={value[item]}
              title={item}
              onRemove={(v) => onRemoveKey(item)}
              isRoot={false}
              disabled={disabled}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default JsonSchemeCreator;
